<template>
    <button
        :aria-selected="props.isActive"
        :aria-controls="`${props.value}-tab-content`"
        :class="classes"
        data-testid="basic-tab-header"
        :disabled="isDisabled"
        class="flex transition-all basic-tab-header max-[370px]:p-3 flex-center py-5 max-sm:p-5 px-[26.5px] cursor-pointer border-b-2 truncate"
        role="tab"
    >
        <div
            v-if="hasIcon"
            class="pr-2.5"
        >
            <slot name="icon">
                <BasicIcon
                    v-if="icon"
                    :name="icon"
                >
                </BasicIcon>
            </slot>
        </div>
        <div class="text-sm truncate">
            <slot>
                <div>
                    {{ label }}
                </div>
            </slot>
        </div>
    </button>
</template>

<script setup lang="ts">
import BasicIcon from '@atoms/BasicIcon/BasicIcon.vue';

import { useIsHydrating } from '@composables/useIsHydrating';

export interface BasicTabHeader {
    icon?: string;
    label?: string;
    value: string;
    isActive?: boolean;
    grow?: boolean;
    superviseHydration?: boolean;
}

const props = withDefaults(defineProps<BasicTabHeader>(), {
    superviseHydration: true,
    icon: undefined,
    label: undefined,
});

const { isHydrating } = useIsHydrating();

export interface BasicTabHeaderSlots {
    default(): unknown;
    icon(): unknown;
}

const slots = defineSlots<BasicTabHeaderSlots>();

if (!props.label && !slots.default) {
    console.warn(
        'Missing label! At least one of: "label" props or default slot has to be passed'
    );
}

const isDisabled = computed(() => {
    return props.superviseHydration && isHydrating.value;
});
const basicClasses = computed(() => {
    if (!props.isActive)
        return [
            'fade',
            'text-font-3',
            'fill-font-3',
            'border-input',
            ...(isDisabled.value
                ? []
                : [
                      'hover:text-font-2',
                      'hover:fill-font-2',
                      'hover:border-input-dark-hover',
                  ]),
        ];
    return ['active', 'border-primary', 'text-font-1', 'fill-font-1'];
});

const growClasses = computed(() => {
    if (!props.grow) return [];
    return ['flex-1', 'justify-center'];
});

const classes = computed(() => {
    return [...growClasses.value, ...basicClasses.value];
});

const hasIcon = computed(() => {
    return !!props.icon || !!slots.icon;
});
</script>
